import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Pricing", href: "#routes" },
  { label: "About", href: "#why" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  const goRoute = (to) => { setOpen(false); navigate(to); };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center">
          <img src="/logo.png" alt="7 Star Limo Services" className="h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            l.to ? (
              <button key={l.to} data-testid={`nav-${l.label.toLowerCase()}`} onClick={() => goRoute(l.to)}
                className="text-sm tracking-wide text-foreground/80 hover:text-primary transition-colors">
                {l.label}
              </button>
            ) : (
              <button key={l.href} data-testid={`nav-${l.label.toLowerCase()}`} onClick={() => go(l.href)}
                className="text-sm tracking-wide text-foreground/80 hover:text-primary transition-colors">
                {l.label}
              </button>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:+19793379282" data-testid="call-now-top"
            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors">
            <Phone size={15} /> 979-337-9282
          </a>
          {user ? (
            <>
              {user.role === "admin" && (
                <Button variant="ghost" data-testid="admin-link" onClick={() => navigate("/admin")}>Admin</Button>
              )}
              <Button variant="ghost" data-testid="dashboard-link" onClick={() => navigate("/dashboard")}>Dashboard</Button>
              <Button variant="outline" data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
                className="border-white/20">Logout</Button>
            </>
          ) : (
            <Button variant="ghost" data-testid="login-link" onClick={() => navigate("/login")}>Sign In</Button>
          )}
          <Button data-testid="booknow-top" onClick={() => go("#book")}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-wide rounded-none">
            Book Now
          </Button>
        </div>

        <button data-testid="mobile-menu-btn" className="lg:hidden text-foreground" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black/95 border-t border-white/10 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            l.to ? (
              <button key={l.to} onClick={() => goRoute(l.to)} className="text-left text-foreground/80 hover:text-primary">{l.label}</button>
            ) : (
              <button key={l.href} onClick={() => go(l.href)} className="text-left text-foreground/80 hover:text-primary">{l.label}</button>
            )
          ))}
          <a href="tel:+19793379282" className="text-primary flex items-center gap-2"><Phone size={16} /> Call or Text 979-337-9282</a>
          {user ? (
            <>
              <Button variant="outline" onClick={() => { setOpen(false); navigate("/dashboard"); }}>Dashboard</Button>
              <Button variant="ghost" onClick={() => { logout(); navigate("/"); }}>Logout</Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }}>Sign In</Button>
          )}
          <Button className="bg-primary text-primary-foreground rounded-none" onClick={() => go("#book")}>Book Now</Button>
        </div>
      )}
    </header>
  );
}
